import { RefObject, useImperativeHandle, useState } from 'react';
import {
  Button,
  Divider,
  Flex,
  Group,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import type { CompulsoryInsurancePenaltyTicketSummary } from '#/components/ui/tickets/compulsory-insurance-penalty/model';
import { TICKET_TYPE, TICKET_TYPE_CONFIG } from '../../../config';
import type { ConfirmPaymentOnBehalfDialogRef } from './useConfirmPaymentOnBehalfDialog';

const CONFIG =
  TICKET_TYPE_CONFIG[TICKET_TYPE.COMPULSORY_INSURANCE_PENALTY_TICKETS];

export function ConfirmPaymentOnBehalfDialog({
  modalRef,
}: {
  modalRef: RefObject<ConfirmPaymentOnBehalfDialogRef | null>;
}) {
  const [state, setState] = useState<
    | {
        ticket: CompulsoryInsurancePenaltyTicketSummary;
        onConfirm: () => void;
        onCancel: () => void;
      }
    | undefined
  >();

  const isOpen = !!state;

  useImperativeHandle(modalRef, () => ({
    confirm: async (ticket: CompulsoryInsurancePenaltyTicketSummary) => {
      const promise = new Promise<boolean>((resolve) => {
        setState({
          ticket,
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });

      const result = await promise;
      setState(undefined);
      return result;
    },
  }));

  const ticket = state?.ticket;

  if (!ticket) return null;

  const handleCancel = state.onCancel;

  return (
    <Modal
      opened={isOpen}
      onClose={handleCancel}
      title={
        <Stack gap={2}>
          <Text fw={600} size="lg">
            確認已代繳
          </Text>
          <Text size="sm" c="dimmed">
            {`${ticket.account.email}．${CONFIG.name}．${ticket.plateNumber}`}
          </Text>
        </Stack>
      }
      size="lg"
    >
      <Stack gap="md">
        <Text size="md" fw={500}>
          {`請確認已透過${CONFIG.queryPlatform}完成線下代繳，提交後此票將標記為「繳款成功」並結案，系統將寄送繳款成功通知。`}
        </Text>
        <Flex
          bg={'rgb(248, 249, 250)'}
          bdrs="lg"
          p="lg"
          direction="column"
          gap="sm"
        >
          <Text size="sm" fw={500} c="dimmed">
            線上代繳金額
          </Text>
          <Text size="30px" fw={700}>
            {`NT$ ${ticket.amount}`}
          </Text>
          {ticket.handlingFee != null && (
            <>
              <Text size="sm" fw={500} c="dimmed">
                手續費
              </Text>
              <Text size="xl" fw={600}>
                {`NT$ ${ticket.handlingFee}`}
              </Text>
            </>
          )}
        </Flex>

        <Divider />

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={handleCancel}>
            取消
          </Button>
          <Button onClick={state.onConfirm}>送出請款</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
